// Finicity Customer Management Service
import { getAuthToken } from './auth/authService';
import { buildApiEndpoint } from './api/config';

export interface CreateConnectUrlResponse {
  connectUrl: string;
}

export interface CreateConnectUrlRequest {
  institutionId: string;
  redirectUri?: string; // Redirect URI - where Finicity will redirect back
}

export class FinicityCustomerService {
  private baseUrl: string;

  constructor() {
    // Use the Sparrow API base URL
    this.baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://api-uat.sparrowcard.com';
  }

  /**
   * Create Finicity customer using Sparrow API
   * Maps to the Sparrow connect endpoint
   */
  async createConnectUrl(request: CreateConnectUrlRequest): Promise<CreateConnectUrlResponse> {
    try {
      // Get Firebase auth token - REQUIRED for API call
      const authToken = await getAuthToken();
      
      // Ensure we have a token before making API call
      if (!authToken) {
        throw new Error('Authentication token not available. Please wait for authentication to complete.');
      }
      
      // Build redirect URI - use provided one or default to /verification
      const redirectUri = request.redirectUri || `${window.location.origin}/verification`;
      
      // Correct format for /connect/lite API
      const requestBody = {
        institution_id: request.institutionId,
        redirectUri: redirectUri
      };

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      };

      const endpoint = buildApiEndpoint('connect/lite');
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`Failed to create connect URL: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Map response to expected format
      return {
        connectUrl: data.connect_url || data.connectUrl || 'https://connect.finicity.com/connect',
      };
    } catch (error) {
      console.error('Error creating Finicity connect URL:', error);
      throw error;
    }
  }

  /**
   * Handle Connect success callback
   * This would typically verify the connection with your backend
   */
  async handleConnectSuccess(accounts: any[]): Promise<void> {
    try {
      // Get Firebase auth token - REQUIRED for API call
      const authToken = await getAuthToken();

      // Ensure we have a token before making API call
      if (!authToken) {
        console.warn('Authentication token not available for connect success callback');
        return; // Don't throw error for success handler to avoid breaking the flow
      }

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      };

      const endpoint = buildApiEndpoint('connect-success/');
      await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          accounts: accounts,
        }),
      });
    } catch (error) {
      console.error('Error handling connect success:', error);
      // Don't throw error for success handler to avoid breaking the flow
      console.warn('Connect success notification failed, but connection may still be valid');
    }
  }
}

export const finicityCustomerService = new FinicityCustomerService(); 